import { ViewConfig } from "@lib/views";
import { DATE_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { HistoryDetailsBaseRow } from "../../cells/historyDetailsBaseRow";
import { LogTimeCell } from "../../cells/LogTimeCell";
import { HistoryAccountValue } from "../utils/coaActivityValues";
import {
    HistoryAccountEntityCell,
    HistoryAccountGroupCell,
    HistoryAccountIdentifierCell,
    HistoryAccountModifiedCell,
    HistoryAccountPreviousCell,
} from "../cells/HistoryAccountCells";
import {
    HistoryAccountDetailsColumn,
    HistoryAccountTableKind,
} from "./historyAccountDetailsView";

export interface HistoryAccountLine {
    id: string;
    label: string | null;
    identifier: string;
    identifierNote: string | null;
    groupPath: string[];
    changedField: string | null;
    previous: HistoryAccountValue;
    modified: HistoryAccountValue;
}

export interface HistoryAccountDetailsRow extends HistoryDetailsBaseRow {
    id: string;
    lines: HistoryAccountLine[];
}

const SINGLE_LINE_NAME_COLUMN_SIZE = { minSize: 200, maxSize: 360 };
const LABELLED_LINES_NAME_COLUMN_SIZE = { minSize: 280, maxSize: 480 };

const NAME_COLUMN_SIZE_BY_KIND: Record<
    HistoryAccountTableKind,
    { minSize: number; maxSize: number }
> = {
    [HistoryAccountTableKind.IDENTITY]: SINGLE_LINE_NAME_COLUMN_SIZE,
    [HistoryAccountTableKind.CHANGE]: SINGLE_LINE_NAME_COLUMN_SIZE,
    [HistoryAccountTableKind.MERGE]: LABELLED_LINES_NAME_COLUMN_SIZE,
    [HistoryAccountTableKind.GROUP_DELETE]: LABELLED_LINES_NAME_COLUMN_SIZE,
};

interface CreateHistoryAccountDetailsViewConfigParams {
    kind: HistoryAccountTableKind;
    changedFieldLabel: string | null;
}

export const createHistoryAccountDetailsViewConfig = ({
    kind,
    changedFieldLabel,
}: CreateHistoryAccountDetailsViewConfigParams): ViewConfig<
    HistoryAccountDetailsRow,
    HistoryAccountDetailsColumn,
    never
> => {
    const previousLabel = changedFieldLabel
        ? `Previous ${changedFieldLabel}`
        : "Previous";
    const modifiedLabel = changedFieldLabel
        ? `Modified ${changedFieldLabel}`
        : "Modified";

    return {
        columns: {
            [HistoryAccountDetailsColumn.TIME]: {
                name: "Time",
                header: "",
                accessorKey: "logDate",
                cell: LogTimeCell,
                ...DATE_COLUMN_SIZE,
            },
            [HistoryAccountDetailsColumn.IDENTIFIER]: {
                name: "Name",
                header: "Name",
                accessorKey: "lines",
                cell: HistoryAccountIdentifierCell,
                ...NAME_COLUMN_SIZE_BY_KIND[kind],
            },
            [HistoryAccountDetailsColumn.GROUP]: {
                name: "Group",
                header: "Group",
                accessorKey: "lines",
                cell: HistoryAccountGroupCell,
                minSize: 160,
                maxSize: 260,
            },
            [HistoryAccountDetailsColumn.ENTITY]: {
                name: "Entity",
                header: "Entity",
                accessorKey: "entityId",
                cell: HistoryAccountEntityCell,
                minSize: 140,
                maxSize: 220,
            },
            [HistoryAccountDetailsColumn.PREVIOUS]: {
                name: previousLabel,
                header: previousLabel,
                accessorKey: "lines",
                cell: HistoryAccountPreviousCell,
                minSize: 160,
                maxSize: 300,
            },
            [HistoryAccountDetailsColumn.MODIFIED]: {
                name: modifiedLabel,
                header: modifiedLabel,
                accessorKey: "lines",
                cell: HistoryAccountModifiedCell,
                minSize: 160,
                maxSize: 300,
            },
        },
    };
};
